/**
 * Core astronomical domain types.
 *
 * Conventions (documented, consistent across the whole engine):
 *
 * - RA (right ascension) is expressed in HOURS [0, 24), unless explicitly
 *   marked as degrees.
 * - Declination is in DEGREES [-90, +90].
 * - Azimuth is in DEGREES [0, 360), measured CLOCKWISE from true north
 *   (0 = north, 90 = east, 180 = south, 270 = west) — the navigation/
 *   cartography convention.
 * - Altitude is in DEGREES [-90, +90]; +90 is the zenith.
 * - All engine timestamps are UTC milliseconds since Unix epoch.
 *   Local time is handled exclusively at the UI layer.
 * - Epoch J2000 means the ICRS/J2000 mean equator and equinox.
 *   Epoch OF_DATE means the true-of-date (precessed) mean equator/equinox.
 */

/** Right ascension + declination of a celestial object. */
export interface CelestialCoordinate {
  /** Right ascension in hours [0, 24). */
  rightAscensionHours: number;
  /** Declination in degrees [-90, +90]. */
  declinationDegrees: number;
  /** Reference epoch of these coordinates. */
  epoch: Epoch;
}

export type Epoch = "J2000" | "OF_DATE";

/** Altitude + azimuth relative to an observer. */
export interface HorizontalCoordinate {
  /** Altitude in degrees, +90 = zenith. */
  altitudeDegrees: number;
  /** Azimuth in degrees, clockwise from north, [0, 360). */
  azimuthDegrees: number;
}

/** A point on (or near) the Earth's surface. */
export interface ObserverLocation {
  /** Latitude in degrees, north positive, [-90, +90]. */
  latitudeDegrees: number;
  /** Longitude in degrees, east positive, [-180, +180]. */
  longitudeDegrees: number;
  /** Elevation above mean sea level in meters (optional). */
  elevationMeters?: number;
}

/** An instant of observation, kept in a single consistent time system (UTC). */
export interface ObserverTime {
  /** UTC milliseconds since the Unix epoch. */
  utcMilliseconds: number;
  /** Julian Date (UT1 ≈ UTC) for this instant. */
  julianDayUt: number;
  /** Julian Date in Terrestrial Time (used by high-precision theories). */
  julianDayTt: number;
  /** Greenwich Mean Sidereal Time in hours [0, 24). */
  siderealHours: number;
}

/** The six classic naked-eye planets plus the outer two. */
export enum Planet {
  Mercury = "Mercury",
  Venus = "Venus",
  Mars = "Mars",
  Jupiter = "Jupiter",
  Saturn = "Saturn",
  Uranus = "Uranus",
  Neptune = "Neptune",
}

/** Identifies a solar-system body that has a dynamically computed position. */
export type SolarSystemBody = "Sun" | "Moon" | Planet;

/** How precise a given computation is claimed to be. */
export type Precision = "high" | "approximate" | "catalog-derived";

/** Geocentric (or topocentric) equatorial position of a solar-system body. */
export interface BodyPosition {
  /** Topocentric equatorial coordinate (of date, aberration corrected). */
  coordinate: CelestialCoordinate;
  /** Distance from the observer, in AU. */
  distanceAu: number;
  /** Visual magnitude (illumination dependent for Moon/planets). */
  magnitude: number;
  /** Angular diameter in arcseconds (0 for unresolved bodies). */
  angularDiameterArcseconds: number;
  /** How this position was computed. */
  precision: Precision;
}
